import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { makeGetApiCall } from "../../shared/makeApiCall";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { startOfDay, endOfDay, parseISO } from "date-fns";
import { makePostApiCall } from "../../shared/makeApiCall";

export default function ProductCalendarViewModel(addSnackbar, logout) {
  const [emptyModal, setEmptyModal] = useState({
    open: false,
    childComponent: null,
  });
  const [calendar, setCalendar] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayHistoryModal, setDayHistoryModal] = useState({
    open: false,
    date: null,
    formattedDate: null,
    items: [],
    loading: false,
  });

  const [analytics, setAnalytics] = useState({
    range: null,
    stats: [],
    loading: true,
  });
  const [filters, setFilters] = useState({
    product: { value: -1, label: "All Products" },
    supervisor: { value: -1, label: "All Employees" },
    employee1: { value: -1, label: "All Employees" },
    employee2: { value: -1, label: "All Employees" },
    certification: { value: -1, label: "All Certifications" },
    recipe: { value: -1, label: "All Recipes" },
    grandTotal: null,
    // default range: today from 12:00 AM to 11:59:59.999 PM
    startDate: startOfDay(new Date()),
    endDate: endOfDay(new Date()),
    product_lot: null,
  });

  const getCalendar = useCallback(
    async (month = currentMonth) => {
      setLoadingCalendar(true);
      const monthStr = format(month, "yyyy-MM");
      try {
        const res = await makeGetApiCall(
          `calendar/manufactured-products/monthly?month=${monthStr}`
        );
        if (res && res.success) {
          const data = Array.isArray(res.data) ? res.data : [];
          setCalendar(data);
          const initiallySelected = data.find((d) => d.isSelected) || null;
          setSelectedDay(initiallySelected);
          return data;
        } else {
          if (res && res.errorStatus === 401) {
            if (logout) logout();
            return;
          }
          setEmptyModal({
            open: true,
            childComponent: (
              <ModelErrorComponent
                error={res}
                close={() =>
                  setEmptyModal({ open: false, childComponent: null })
                }
              />
            ),
          });
        }
      } catch (err) {
        setEmptyModal({
          open: true,
          childComponent: (
            <ModelErrorComponent
              error={err}
              close={() => setEmptyModal({ open: false, childComponent: null })}
            />
          ),
        });
        if (addSnackbar) addSnackbar("Failed to load calendar", "error");
      } finally {
        setLoadingCalendar(false);
      }
    },
    [currentMonth, addSnackbar, logout]
  );

  const getAnalytics = async function (date) {
    setAnalytics({ ...analytics, loading: true });
    const parsedDate = parseISO(date);
    filters.startDate = startOfDay(parsedDate);
    filters.endDate = endOfDay(parsedDate);

    const payload = {
      filters: { ...filters },
    };
    let callResult = await makePostApiCall(
      `fetchManufacturingAnalytics`,
      payload
    );
    setAnalytics({ ...analytics, loading: false });
    if (callResult.success) {
      setAnalytics({ ...analytics, ...callResult.data });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getDayHistory = useCallback(
    async (date) => {
      if (!date) return;
      setDayHistoryModal((s) => ({ ...s, loading: true, date }));
      try {
        // preferred: call daily endpoint with timezone (API docs require tz optional param)
        const tz =
          Intl?.DateTimeFormat?.()?.resolvedOptions?.().timeZone ||
          "America/New_York";
        const res = await makeGetApiCall(
          `calendar/manufactured-products/daily?date=${date}&tz=${encodeURIComponent(
            tz
          )}`
        );
        if (res && res.success) {
          let items = res.data.events;
          setDayHistoryModal({
            open: true,
            date,
            formattedDate: res.data.formatted_date,
            items,
            loading: false,
          });
        } else {
          if (res && res.errorStatus === 401) {
            if (logout) logout();
            return;
          }
          setEmptyModal({
            open: true,
            childComponent: (
              <ModelErrorComponent
                error={res}
                close={() =>
                  setEmptyModal({ open: false, childComponent: null })
                }
              />
            ),
          });
          setDayHistoryModal((s) => ({ ...s, loading: false }));
        }
      } catch (err) {
        setEmptyModal({
          open: true,
          childComponent: (
            <ModelErrorComponent
              error={err}
              close={() => setEmptyModal({ open: false, childComponent: null })}
            />
          ),
        });
        setDayHistoryModal((s) => ({ ...s, loading: false }));
      }
    },
    [logout]
  );

  const openDayHistory = (date) => {
    // open and fetch
    getDayHistory(date);
  };

  const handleDaySelect = (selectedDate) => {
    setCalendar((prev) =>
      prev.map((day) => ({ ...day, isSelected: day.date === selectedDate }))
    );
    const found = calendar.find((d) => d.date === selectedDate) || null;
    setSelectedDay(found);
  };

  const onChangeMonth = (newMonth) => {
    const doChange = async () => {
      setCurrentMonth(newMonth);
      const data = await getCalendar(newMonth);
      try {
        const today = new Date();
        const monthFormat = format(newMonth, "yyyy-MM");
        const todayMonth = format(today, "yyyy-MM");
        if (monthFormat === todayMonth && Array.isArray(data)) {
          const todayStr = format(today, "yyyy-MM-dd");
          const updated = data.map((d) => ({
            ...d,
            isSelected: d.date === todayStr,
            isToday: d.date === todayStr,
          }));
          setCalendar(updated);
          const found = updated.find((d) => d.isSelected) || null;
          setSelectedDay(found);
        }
      } catch (err) {
        // ignore
      }
    };
    doChange();
  };

  const openEventDetails = (event, EventDetailsComponent) => {
    const Child = EventDetailsComponent
      ? EventDetailsComponent
      : function DefaultEventDetails({ event: ev, closeModal }) {
          return (
            <div className="p-4">
              <h3 className="text-lg font-semibold">Production run</h3>
              <div className="mt-2 text-sm">
                <div>
                  <strong>Product:</strong> {ev.product_name || "-"}
                </div>
                <div>
                  <strong>Lot:</strong> {ev.new_lot_number || "-"}
                </div>
                <div>
                  <strong>Time:</strong> {ev.time || ev.production_date || "-"}
                </div>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => closeModal && closeModal()}
                  className="px-3 py-1 bg-blue-600 text-white rounded"
                >
                  Close
                </button>
              </div>
            </div>
          );
        };

    setEmptyModal({
      open: true,
      childComponent: (
        <Child
          event={event}
          closeModal={() =>
            setEmptyModal({ open: false, childComponent: null })
          }
        />
      ),
    });
  };

  useEffect(() => {
    getCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    emptyModal,
    calendar,
    loadingCalendar,
    currentMonth,
    selectedDay,
    getCalendar,
    onChangeMonth,
    handleDaySelect,
    openEventDetails,
    dayHistoryModal,
    setDayHistoryModal,
    getDayHistory,
    openDayHistory,
    setSelectedDay,
    setEmptyModal,
    setCalendar,
    analytics,
    getAnalytics,
  };
}
